"use client";

import React from "react";
import { motion } from "framer-motion";
import { MapPin, TrendingUp, Target, Search, CircleCheck as CheckCircle2, ArrowRight, ChartBar as BarChart3, Users } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
const marketInsights = [{
  id: 1,
  title: "Local Search Behavior",
  description: "Belfast buyers rely heavily on localized Google search queries and Google Maps to find immediate B2B services, professional consultants, and retail options.",
  badgeText: "High Commercial Intent",
  icon: MapPin
}, {
  id: 2,
  title: "Unrealized Market Gap",
  description: "Many Northern Ireland SMEs rely predominantly on social media or word-of-mouth, leaving high-value Google organic search rankings wide open for structured SEO capture.",
  badgeText: "Low Technical Competition",
  icon: TrendingUp
}, {
  id: 3,
  title: "District-Level Targeting",
  description: "Search demand varies across key Belfast commercial zones including Cathedral Quarter, Titanic Quarter, South Belfast, and outer commuter corridors.",
  badgeText: "Hyper-Local Relevance",
  icon: Target
}];
const marketStats = [{
  id: 1,
  value: "78%",
  label: "Immediate Action",
  description: "Of local search queries in Belfast result in an direct call, quote request, or direct visit within 24 hours."
}, {
  id: 2,
  value: "3.4x",
  label: "Higher Organic ROI",
  description: "Achieved by localized SEO strategies compared to traditional regional print or digital display campaign spending."
}, {
  id: 3,
  value: "64%",
  label: "Map Pack Trust",
  description: "Of Belfast decision-makers bypass paid ads to choose top-three verified organic Google Local Map Pack results."
}];
const strategicSteps = [{
  id: 1,
  title: "Belfast Keyword Mapping",
  text: "Identifying localized commercial search terms used by Northern Ireland buyers."
}, {
  id: 2,
  title: "Technical Local Optimization",
  text: "Aligning your website's technical architecture with search engine geographical markers."
}, {
  id: 3,
  title: "Google Business Profile Dominance",
  text: "Optimizing local citations, reviews, and entity authority across the region."
}, {
  id: 4,
  title: "High-Authority NI Backlinks",
  text: "Earning genuine local references and media coverage across trusted regional sources."
}];
export default function Belfast() {
  return <section id="belfastmarketlandscape" data-nav="light" className="bg-background text-foreground py-20 md:py-28 relative overflow-hidden">
      <div className="container mx-auto px-4 md:px-6 max-w-7xl relative z-10">
        {}
        <div className="max-w-3xl mx-auto text-center mb-16 md:mb-20">
          <motion.div initial={{
          opacity: 0,
          y: 15
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5
        }}>
            <Badge variant="secondary" className="mb-4 text-xs font-semibold uppercase tracking-wider px-3 py-1 bg-secondary text-secondary-foreground border border-accent/20">
              Regional Market Intelligence
            </Badge>
          </motion.div>

          <motion.h2 initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5,
          delay: 0.1
        }} className="text-2xl sm:text-3xl lg:text-4xl font-bold font-serif text-primary tracking-tight">
            Belfast Search Landscape & Market Opportunity
          </motion.h2>

          <motion.p initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5,
          delay: 0.2
        }} className="mt-4 text-lg md:text-xl text-muted-foreground leading-relaxed">
            When you work directly with me, I analyze exact local search behaviors across Northern Ireland to build tailored SEO strategies that convert high-intent searchers into loyal clients.
          </motion.p>
        </div>

        {}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
          {marketInsights.map((insight, i) => {
          const IconComponent = insight.icon;
          return <motion.div key={insight.id} data-index={i} initial={{
            opacity: 0,
            y: 25
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: i * 0.1
          }}>
                <Card className="h-full bg-card text-card-foreground border-border shadow-sm hover:shadow-md transition-all duration-300 flex flex-col justify-between">
                  <CardHeader className="pb-3">
                    <div className="w-12 h-12 rounded-lg bg-secondary flex items-center justify-center mb-4 text-primary">
                      <IconComponent className="w-6 h-6" />
                    </div>
                    <div className="flex items-center justify-between gap-2 mb-2">
                      <Badge variant="outline" className="text-xs border-accent/40 text-foreground bg-accent/10">
                        {insight.badgeText}
                      </Badge>
                    </div>
                    <CardTitle className="text-xl font-bold text-primary font-serif">
                      {insight.title}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-muted-foreground text-sm leading-relaxed">
                      {insight.description}
                    </p>
                  </CardContent>
                </Card>
              </motion.div>;
        })}
        </div>

        {}
        <div className="bg-card border border-border rounded-2xl p-8 md:p-10 mb-16 shadow-sm">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 divide-y md:divide-y-0 md:divide-x divide-border">
            {marketStats.map((stat, i) => <div key={stat.id} data-index={i} className={`flex flex-col items-center text-center ${i > 0 ? "pt-6 md:pt-0 md:pl-8" : ""}`}>
                <div className="text-4xl md:text-5xl font-extrabold text-primary font-serif mb-2">
                  {stat.value}
                </div>
                <div className="text-base font-semibold text-foreground mb-2">
                  {stat.label}
                </div>
                <p className="text-xs text-muted-foreground leading-relaxed max-w-xs">
                  {stat.description}
                </p>
              </div>)}
          </div>
        </div>

        {}
        <div className="bg-muted/60 border border-border rounded-2xl p-8 md:p-12 mb-16 relative">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
            <div className="lg:col-span-7">
              <Badge className="bg-accent text-accent-foreground mb-4">
                Direct Expert Execution
              </Badge>
              <h3 className="text-2xl md:text-3xl font-bold font-serif text-primary mb-4">
                How I Capture Belfast Search Share for Your Business
              </h3>
              <p className="text-foreground/90 text-sm md:text-base leading-relaxed mb-6">
                You work directly with me—never account managers or junior staff. I apply proven technical and content methodologies directly tailored to Belfast and Northern Ireland target audiences.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {strategicSteps.map((step, i) => <div key={step.id} data-index={i} className="flex items-start gap-2.5">
                    <CheckCircle2 className="w-5 h-5 text-primary shrink-0 mt-0.5" />
                    <div>
                      <h4 className="text-xs font-semibold text-foreground">{step.title}</h4>
                      <p className="text-xs text-muted-foreground">{step.text}</p>
                    </div>
                  </div>)}
              </div>
            </div>

            <div className="lg:col-span-5 flex flex-col justify-center items-center bg-card border border-border p-6 md:p-8 rounded-xl shadow-xs text-center">
              <div className="w-16 h-16 rounded-full bg-secondary flex items-center justify-center text-primary mb-4">
                <Search className="w-8 h-8" />
              </div>
              <h4 className="text-lg font-bold text-primary font-serif mb-2">
                Ready to Claim Top Rankings in Belfast?
              </h4>
              <p className="text-xs text-muted-foreground mb-6 leading-relaxed">
                Book a direct SEO consultation with me to discover untapped keywords and competitor gaps in your specific market.
              </p>
              <Button asChild className="w-full sm:w-auto">
                <Link to="/ContactPage">
                  Book a Free SEO Consultation
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Link>
              </Button>
            </div>
          </div>
        </div>

        <Separator className="my-8" />

        {}
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4 text-sm text-muted-foreground">
          <p>Looking for specialized service solutions across Northern Ireland?</p>
          <div className="flex items-center gap-4">
            <Button variant="link" size="sm" asChild className="text-primary font-medium p-0 h-auto">
              <Link to="/LocalSEOPage">Explore Local SEO Services</Link>
            </Button>
            <span className="text-border">|</span>
            <Button variant="link" size="sm" asChild className="text-primary font-medium p-0 h-auto">
              <Link to="/CaseStudiesPage">View Proven Case Studies</Link>
            </Button>
          </div>
        </div>
      </div>
    </section>;
}